import axios, { AxiosError, type AxiosRequestConfig } from 'axios'
import type { ApiResponse, ErrorResponse } from '@jiaofa/api-types'
import { getAdminMockResponse, isAdminMockEnabled } from './mock'

const TOKEN_KEY = 'jiaofa-admin-token'
let requestSequence = 0

const generateRequestId = (): string => {
  const randomUUID = globalThis.crypto?.randomUUID
  if (typeof randomUUID === 'function') return randomUUID.call(globalThis.crypto)

  requestSequence = (requestSequence + 1) % 1000000
  return `${Date.now()}-${String(requestSequence).padStart(6, '0')}-${Math.random()
    .toString(36)
    .slice(2, 10)}`
}

export class ApiError extends Error {
  constructor(message: string, public readonly code?: number, public readonly requestId?: string) {
    super(message)
    this.name = 'ApiError'
  }
}

const client = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 15000,
})

/**
 * GET 请求追加时间戳参数，绕开浏览器缓存。
 *
 * 保存走的是 PUT，浏览器不会因此失效已缓存的 GET，于是保存后重新拉列表仍拿到旧数据，
 * 表现为「要手动刷新页面才更新」。只对 GET 加，写请求不动。
 */
export function withCacheBuster(config: AxiosRequestConfig): AxiosRequestConfig {
  const method = (config.method ?? 'get').toString().toLowerCase()
  if (method !== 'get') return config
  return {
    ...config,
    params: { ...((config.params as Record<string, unknown> | undefined) ?? {}), _t: Date.now() },
  }
}

client.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY)
  config.headers.set('x-request-id', generateRequestId())
  if (token) config.headers.set('Authorization', `Bearer ${token}`)
  config.params = withCacheBuster(config).params
  return config
})

client.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ErrorResponse>) => {
    const status = error.response?.status
    if (status === 401) localStorage.removeItem(TOKEN_KEY)
    return Promise.reject(new ApiError(error.response?.data?.message ?? error.message, status, error.response?.data?.requestId))
  },
)

export const request = async <TResponse, TData = unknown>(
  config: AxiosRequestConfig<TData>,
): Promise<TResponse> => {
  if (isAdminMockEnabled()) {
    const mockResponse = getAdminMockResponse(config)
    if (mockResponse.handled) return mockResponse.data as TResponse
  }
  const response = await client.request<ApiResponse<TResponse>>(config)
  if (response.data.code !== 0) {
    throw new ApiError(response.data.message || '业务处理失败', response.data.code, response.data.requestId)
  }
  return response.data.data
}
